import React, { useEffect, useContext, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

// My Files
import { DatabaseContext, UserContext } from "../../context";
import ToolbarWithLogo from "../ToolbarWithLogo";
import firebase from "../../firebase";
import ProjectTitleComponent from "./Components/ProjectTitleComponent";
import ProjectTextBoxComponent from "./Components/ProjectTextBoxComponent";
import ProjectPlaylistComponent from "./Components/ProjectPlaylistComponent";
import ProjectFileComponent from "./Components/ProjectFileComponent";
import NoPermissionsMessage from "../NoPermissionsMessage";

const surfaceSx = {
    borderRadius: 3,
    borderColor: "divider",
    backgroundColor: "background.paper",
    boxShadow: "0 8px 28px rgba(20, 35, 25, 0.065)",
};

export default function ProjectViewPage(props) {
    const { currentUserData } = useContext(UserContext);
    const location = useLocation();
    const history = useHistory();

    const {
        projectInfo,
        projectInfoLoaded = false,
        groups,
        groupsLoaded = false,
    } = useContext(DatabaseContext);

    const [title, setTitle] = useState("");
    const [introduction, setIntroduction] = useState("");

    const [project, setProject] = useState();
    const [projectCreatedBy, setProjectCreatedBy] = useState();

    const [canEdit, setCanEdit] = useState(false);
    const [userHasAccess, setUserHasAccess] = useState(false);
    const [accessResolved, setAccessResolved] = useState(false);

    const getProject = useCallback(
        async (key) => {
            let projectRef = firebase.database().ref("projects").child(key);

            if (!project) {
                console.log("Reading Project from Firebase");

                projectRef.on("value", function (snapshot) {
                    let key = snapshot.key;
                    let data = snapshot.val();

                    let componentsArray = [];

                    if (data && data.components) {
                        let componentKeys = Object.keys(data.components);
                        let componentValues = Object.values(data.components);

                        for (let i = 0; i < componentKeys.length; i++) {
                            var commentsArray = [];
                            if (componentValues[i].comments) {
                                let commentKeys = Object.keys(
                                    componentValues[i].comments
                                );
                                let commentValues = Object.values(
                                    componentValues[i].comments
                                );

                                for (let j = 0; j < commentKeys.length; j++) {
                                    const comment = commentValues[j];
                                    comment.key = commentKeys[j];
                                    commentsArray.push(comment);
                                }
                            }

                            let componentObject = {
                                key: componentKeys[i],
                                row: componentValues[i].row,
                                type: componentValues[i].type,
                                fileType: componentValues[i].fileType,
                                text: componentValues[i].text,
                                linkKey: componentValues[i].linkKey,
                                addedBy: componentValues[i].addedBy,
                                addedByName: componentValues[i].addedByName,
                                addedOn: componentValues[i].addedOn,
                                allowComments: componentValues[i].allowComments,
                                comments: commentsArray,
                            };

                            componentsArray.push(componentObject);
                        }
                    }

                    let project = {
                        key: key,
                        components: componentsArray.sort((a, b) =>
                            a.row > b.row ? 1 : -1
                        ),
                    };

                    // console.log(project);
                    setProject(project);
                });
            } else {
                console.log("Already Read Project");
            }
        },
        [project]
    );
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);

        const key = urlParams.get("key");

        let matchingInfo = projectInfo.find((p) => p.key === key);

        if (
            matchingInfo &&
            currentUserData &&
            groupsLoaded &&
            projectInfoLoaded
        ) {
            let hasAccess = false;

            // Check if user created project

            if (matchingInfo.createdBy === currentUserData.key) {
                hasAccess = true;
                setCanEdit(true);
            }

            // Check if user was added to project individually
            let matchingUser = matchingInfo.users.find(
                (u) => u.key === currentUserData.key
            );

            if (matchingUser) {
                hasAccess = true;
                if (matchingUser.canEdit) {
                    setCanEdit(true);
                }
            }

            // Check if user is in a group that was added to the project
            matchingInfo.groups.forEach((projectGroup) => {
                let matchingGroup = groups.find(
                    (g) => g.key === projectGroup.key
                );
                if (matchingGroup) {
                    if (
                        matchingGroup.users.filter(
                            (u) => u.key === currentUserData.key
                        ).length > 0
                    ) {
                        hasAccess = true;

                        if (projectGroup.canEdit) {
                            setCanEdit(true);
                        }
                    }
                }
            });

            if (hasAccess) {
                setTitle(matchingInfo.title);
                setIntroduction(matchingInfo.intro);
                setProjectCreatedBy(matchingInfo.createdBy);
                getProject(key);
                setUserHasAccess(true);
            } else {
                setUserHasAccess(false);
            }

            if (project) {
                console.log("Have read project succesfully");
            }
            setAccessResolved(true);
        } else if (currentUserData && groupsLoaded && projectInfoLoaded) {
            setAccessResolved(true);
        }

        // eslint-disable-next-line
    }, [currentUserData, projectInfo, project, projectInfoLoaded]);

    const isLoading =
        !accessResolved || (userHasAccess && !project);

    return (
        <>
            {isLoading && (
                <Box
                    sx={{
                        minHeight: {
                            xs: "calc(100dvh - 60px)",
                            sm: "calc(100vh - 128px)",
                        },
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: "background.default",
                    }}
                >
                    <Box sx={{ textAlign: "center", color: "text.secondary" }}>
                        <CircularProgress
                            size={32}
                            aria-label="Loading project"
                        />
                        <Typography variant="body2" sx={{ mt: 1.5 }}>
                            Loading project…
                        </Typography>
                    </Box>
                </Box>
            )}

            {userHasAccess && project && (
                <Box
                    component="main"
                    sx={{
                        minHeight: {
                            xs: "calc(100dvh - 60px)",
                            sm: "calc(100vh - 128px)",
                        },
                        pb: 12,
                        backgroundColor: "background.default",
                    }}
                >
                    <Container
                        maxWidth="lg"
                        sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 3,
                            }}
                        >
                            <Card variant="outlined" sx={surfaceSx}>
                                <CardContent
                                    sx={{
                                        p: { xs: 2, sm: 3 },
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        "&:last-child": {
                                            pb: { xs: 2, sm: 3 },
                                        },
                                    }}
                                >
                                    <IconButton
                                        aria-label="Back to projects"
                                        onClick={() => history.goBack()}
                                        sx={{
                                            flexShrink: 0,
                                            border: "1px solid",
                                            borderColor: "divider",
                                        }}
                                    >
                                        <ArrowBackIcon />
                                    </IconButton>
                                    <Box
                                        sx={{
                                            width: 46,
                                            height: 46,
                                            display: {
                                                xs: "none",
                                                sm: "grid",
                                            },
                                            placeItems: "center",
                                            flexShrink: 0,
                                            borderRadius: 2,
                                            color: "primary.main",
                                            backgroundColor:
                                                "rgba(0, 149, 61, 0.09)",
                                        }}
                                    >
                                        <FolderOutlinedIcon />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="h5"
                                            component="h1"
                                            sx={{ lineHeight: 1.1 }}
                                        >
                                            {title || "Untitled project"}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 0.5 }}
                                        >
                                            Project
                                        </Typography>
                                    </Box>
                                    <Chip
                                        size="small"
                                        label={`${project.components.length} ${
                                            project.components.length === 1
                                                ? "item"
                                                : "items"
                                        }`}
                                        sx={{
                                            display: {
                                                xs: "none",
                                                sm: "flex",
                                            },
                                            backgroundColor:
                                                "rgba(0, 149, 61, 0.09)",
                                            color: "primary.main",
                                        }}
                                    />
                                </CardContent>
                            </Card>

                            {introduction && (
                                <Card variant="outlined" sx={surfaceSx}>
                                    <CardContent
                                        sx={{
                                            p: { xs: 2.5, sm: 3 },
                                            "&:last-child": {
                                                pb: { xs: 2.5, sm: 3 },
                                            },
                                        }}
                                    >
                                        <Typography
                                            variant="overline"
                                            color="primary.dark"
                                            sx={{ fontWeight: 800 }}
                                        >
                                            About this project
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                mt: 0.75,
                                                whiteSpace: "pre-wrap",
                                                overflowWrap: "anywhere",
                                            }}
                                        >
                                            {introduction}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            )}

                            {project.components.length === 0 ? (
                                <Card variant="outlined" sx={surfaceSx}>
                                    <Box
                                        sx={{
                                            minHeight: 220,
                                            px: 3,
                                            py: 5,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textAlign: "center",
                                            color: "text.secondary",
                                        }}
                                    >
                                        <FolderOutlinedIcon
                                            sx={{
                                                fontSize: 44,
                                                opacity: 0.42,
                                            }}
                                        />
                                        <Typography
                                            variant="h6"
                                            color="text.primary"
                                            sx={{ mt: 1 }}
                                        >
                                            This project is empty
                                        </Typography>
                                    </Box>
                                </Card>
                            ) : (
                                project.components.map((component) => (
                                    <React.Fragment key={component.key}>
                                        {component.type === "Title" && (
                                            <ProjectTitleComponent
                                                projectKey={project.key}
                                                isEditing={false}
                                                component={component}
                                            />
                                        )}
                                        {component.type === "Textbox" && (
                                            <ProjectTextBoxComponent
                                                projectKey={project.key}
                                                isEditing={false}
                                                canEdit={canEdit}
                                                component={component}
                                            />
                                        )}
                                        {component.type === "Playlist" && (
                                            <ProjectPlaylistComponent
                                                projectKey={project.key}
                                                projectCreatedBy={
                                                    projectCreatedBy
                                                }
                                                isEditing={false}
                                                component={component}
                                            />
                                        )}
                                        {component.type === "File" && (
                                            <ProjectFileComponent
                                                projectKey={project.key}
                                                isEditing={false}
                                                component={component}
                                            />
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </Box>
                    </Container>
                </Box>
            )}

            {accessResolved && !userHasAccess && <NoPermissionsMessage />}
            <ToolbarWithLogo />
        </>
    );
}
